import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Archive,
  Check,
  Film,
  FileVideo,
  ImageOff,
  Loader2,
  Pencil,
  Settings2,
  Upload,
  ChevronsUpDown,
  ExternalLink,
  X,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const TEMPLATES_KEY = "/api/admin/video-reference-templates";
const TEMPLATE_PAGE_SIZE = 12;
type ReferenceKind = "ugc" | "3d" | null;

function KindControl({ kind, testId, onChange, disabled }: {
  kind: string | null | undefined;
  testId: string;
  onChange: (kind: ReferenceKind) => Promise<unknown>;
  disabled?: boolean;
}) {
  const [selected, setSelected] = useState<ReferenceKind>(kind === "ugc" || kind === "3d" ? kind : null);
  useEffect(() => setSelected(kind === "ugc" || kind === "3d" ? kind : null), [kind]);
  return (
    <label className="flex items-center gap-2 text-xs">
      <span>Category</span>
      <select
        aria-label="Reference category"
        data-testid={testId}
        className="h-8 rounded-md border bg-background px-2 text-xs"
        value={selected ?? ""}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value ? event.target.value as ReferenceKind : null;
          setSelected(next);
          void onChange(next).catch(() => setSelected(kind === "ugc" || kind === "3d" ? kind : null));
        }}
      >
        <option value="">Uncategorized</option>
        <option value="ugc">UGC</option>
        <option value="3d">3D</option>
      </select>
    </label>
  );
}

type TemplateOwner = {
  id?: string;
  name?: string;
  type?: "master" | "brand";
};

type VideoReferenceTemplate = {
  id: string;
  kind?: string | null;
  name: string;
  fileName?: string | null;
  previewUrl?: string | null;
  referenceVideoUrl?: string | null;
  thumbnailUrl?: string | null;
  durationSec?: number | null;
  referenceDurationSec?: number | null;
  archivedAt?: string | null;
  owner?: TemplateOwner | null;
  ownerBrandId?: string | null;
  ownerBrandName?: string | null;
  brandId?: string | null;
  brandName?: string | null;
  assignmentCount?: number | null;
  assignedBrandCount?: number | null;
};

type TemplateBrandGrant = {
  brandId: string;
  brandName: string;
  granted: boolean;
};

type TemplateOwnerBrand = {
  id: string;
  name: string;
};

function OwnerCombobox({
  id,
  value,
  brands,
  onChange,
  disabled,
  compact = false,
  testId,
}: {
  id: string;
  value: string;
  brands: TemplateOwnerBrand[];
  onChange: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const selectedBrand = brands.find((brand) => brand.id === value);
  const label = selectedBrand ? `${selectedBrand.name} only` : "All brands";

  const selectOwner = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={`${compact ? "h-8 px-2 text-xs" : "h-10 px-3 text-sm"} w-full justify-between bg-background font-normal`}
          data-testid={testId}
        >
          <span className="min-w-0 truncate text-left">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[18rem] p-0"
      >
        <Command>
          <CommandInput placeholder="Search brands…" data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>No matching brands found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="All brands unrestricted"
                onSelect={() => selectOwner("")}
              >
                <Check className={`mr-2 h-4 w-4 ${value ? "opacity-0" : "opacity-100"}`} />
                All brands
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Brands">
              {brands.map((brand) => (
                <CommandItem
                  key={brand.id}
                  value={`${brand.name} ${brand.id}`}
                  onSelect={() => selectOwner(brand.id)}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === brand.id ? "opacity-100" : "opacity-0"}`} />
                  {brand.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type TemplateGrantResponse = {
  brands: TemplateBrandGrant[];
  restricted: boolean;
  assignmentCount: number;
};

type TemplateUploadResponse = {
  referenceVideoUrl: string;
  referenceDurationSec: number;
  thumbnailUrl: string | null;
  width: number;
  height: number;
};

function unwrapTemplates(value: unknown): VideoReferenceTemplate[] {
  if (Array.isArray(value)) return value as VideoReferenceTemplate[];
  if (value && typeof value === "object") {
    const envelope = value as { templates?: unknown };
    if (Array.isArray(envelope.templates)) return envelope.templates as VideoReferenceTemplate[];
  }
  return [];
}

function unwrapGrants(value: unknown): TemplateGrantResponse {
  const rows = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? (value as { brands?: unknown }).brands
      : [];
  const brands = !Array.isArray(rows) ? [] : rows.map((row) => {
    const item = row as Record<string, unknown>;
    return {
      brandId: String(item.brandId ?? item.id ?? ""),
      brandName: String(item.brandName ?? item.name ?? "Unnamed brand"),
      granted: item.granted === true || item.hasAccess === true || item.assigned === true,
    };
  }).filter((row) => row.brandId);
  const envelope = value && typeof value === "object" && !Array.isArray(value)
    ? value as { restricted?: unknown; assignmentCount?: unknown }
    : {};
  return {
    brands,
    restricted: envelope.restricted === true || brands.some((row) => row.granted),
    assignmentCount: typeof envelope.assignmentCount === "number"
      ? envelope.assignmentCount
      : brands.filter((row) => row.granted).length,
  };
}

function formatDuration(seconds: number | null | undefined) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) {
    return "Duration unavailable";
  }
  const rounded = Math.round(seconds);
  const minutes = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  return minutes ? `${minutes}:${String(remainder).padStart(2, "0")}` : `0:${String(remainder).padStart(2, "0")}`;
}

function brandAccessLabel(template: VideoReferenceTemplate) {
  const brandName = template.ownerBrandName || template.brandName || template.owner?.name;
  const brandId = template.ownerBrandId || template.brandId || (template.owner?.type === "brand" ? template.owner.id : null);
  if (brandName) return `${brandName} only`;
  if (brandId) return "One brand";
  const assignmentCount = template.assignmentCount ?? template.assignedBrandCount ?? 0;
  return assignmentCount > 0
    ? `${assignmentCount} assigned brand${assignmentCount === 1 ? "" : "s"}`
    : "All brands";
}

function BrandGrantsDialog({
  template,
  open,
  onOpenChange,
}: {
  template: VideoReferenceTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const grantsKey = `${TEMPLATES_KEY}/${template.id}/brands`;
  const { data, isLoading, error } = useQuery<TemplateGrantResponse>({
    queryKey: [grantsKey],
    enabled: open,
    queryFn: async () => {
      const response = await apiRequest("GET", grantsKey);
      return unwrapGrants(await response.json());
    },
  });
  const grants = data?.brands ?? [];
  const updateGrant = useMutation({
    mutationFn: async ({ brandId, granted }: { brandId: string; granted: boolean }) => {
      await apiRequest(granted ? "POST" : "DELETE", `${grantsKey}/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [grantsKey] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
    onError: (error: Error) => toast({
      title: "Could not update brand access",
      description: error.message,
      variant: "destructive",
    }),
  });
  const clearGrants = useMutation({
    mutationFn: () => apiRequest("DELETE", `${TEMPLATES_KEY}/${template.id}/assignments`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [grantsKey] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast({ title: "Template is now available to all brands" });
    },
    onError: (error: Error) => toast({
      title: "Could not clear brand assignments",
      description: error.message,
      variant: "destructive",
    }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid={`dialog-template-grants-${template.id}`}>
        <DialogHeader>
          <DialogTitle>Brand assignments</DialogTitle>
          <DialogDescription>
            Choose which brands may use “{template.name}”. With no assignments, it is available to every brand.
          </DialogDescription>
        </DialogHeader>
        {data?.restricted && (
          <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3">
            <p className="text-xs text-muted-foreground">
              Available only to {data.assignmentCount} assigned brand{data.assignmentCount === 1 ? "" : "s"}.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => clearGrants.mutate()}
              disabled={clearGrants.isPending}
              data-testid={`button-clear-template-grants-${template.id}`}
            >
              {clearGrants.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Make available to all
            </Button>
          </div>
        )}
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading brands…
          </div>
        ) : error ? (
          <p className="py-4 text-sm text-destructive" role="alert">
            {(error as Error).message}
          </p>
        ) : grants.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No brands are available.</p>
        ) : (
          <div className="max-h-[420px] space-y-1 overflow-y-auto">
            {grants.map((grant) => (
              <div
                key={grant.brandId}
                className="flex items-center justify-between gap-3 rounded-md p-2 hover:bg-muted"
                data-testid={`row-template-grant-${template.id}-${grant.brandId}`}
              >
                <Label
                  htmlFor={`template-grant-${template.id}-${grant.brandId}`}
                  className="min-w-0 flex-1 truncate text-sm font-medium"
                >
                  {grant.brandName}
                </Label>
                <div className="flex items-center gap-2">
                  {grant.granted && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                  <Checkbox
                    id={`template-grant-${template.id}-${grant.brandId}`}
                    checked={grant.granted}
                    onCheckedChange={(checked) => updateGrant.mutate({
                      brandId: grant.brandId,
                      granted: checked === true,
                    })}
                    disabled={updateGrant.isPending}
                    data-testid={`checkbox-template-grant-${template.id}-${grant.brandId}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TemplatePreview({ template }: { template: VideoReferenceTemplate }) {
  const previewUrl = template.previewUrl || template.referenceVideoUrl;
  const [failed, setFailed] = useState(!previewUrl);
  const [phonePreviewOpen, setPhonePreviewOpen] = useState(false);

  useEffect(() => setFailed(!previewUrl), [previewUrl]);

  if (failed) {
    return (
      <div
        className="flex aspect-[4/5] items-center justify-center rounded-lg bg-muted text-muted-foreground"
        data-testid={`fallback-template-preview-${template.id}`}
      >
        <div className="text-center">
          <ImageOff className="mx-auto h-7 w-7" />
          <p className="mt-1 text-xs">Preview unavailable</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <div
        className="relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-lg bg-black"
        data-testid={`template-reference-preview-frame-${template.id}`}
      >
        <video
          src={previewUrl!}
          poster={template.thumbnailUrl || undefined}
          controls
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full bg-black object-contain"
          onError={() => setFailed(true)}
          aria-label={`Preview ${template.name}`}
          data-testid={`video-template-preview-${template.id}`}
        />
        <div
          className="absolute right-3 top-3 z-10"
          data-testid={`phone-preview-position-template-${template.id}`}
        >
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="aspect-square shrink-0 rounded-full border border-white/15 bg-neutral-900/80 p-0 text-white shadow-lg backdrop-blur-sm hover:scale-105 hover:bg-neutral-900 hover:text-white"
            style={{ width: 44, height: 44, minWidth: 44 }}
            aria-label={`Open ${template.name} in phone preview`}
            title="Open phone preview"
            onClick={() => setPhonePreviewOpen(true)}
            data-testid={`button-phone-preview-template-${template.id}`}
          >
            <ExternalLink className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
        <span className="pointer-events-none absolute bottom-2 right-2 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {formatDuration(template.durationSec ?? template.referenceDurationSec)}
        </span>
      </div>
      <Dialog open={phonePreviewOpen} onOpenChange={setPhonePreviewOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-[380px] p-5 sm:max-w-[380px]"
          data-testid={`dialog-phone-preview-template-${template.id}`}
        >
          <DialogHeader className="pr-6">
            <DialogTitle>{template.name || "Video preview"}</DialogTitle>
            <DialogDescription className="sr-only">
              Autoplaying video with playback controls
            </DialogDescription>
          </DialogHeader>
          <div className="mx-auto w-full max-w-[300px] min-w-0" data-testid="frame-phone">
            <div className="rounded-[2rem] border border-border bg-foreground/90 p-[6px] shadow-lg dark:bg-black">
              <div className="relative overflow-hidden rounded-[1.65rem] bg-black">
                <div className="relative flex items-center justify-between bg-black px-4 pb-1 pt-1.5 text-[9px] font-medium text-white/80">
                  <span>9:41</span>
                  <span className="absolute left-1/2 top-1 h-3.5 w-16 -translate-x-1/2 rounded-full bg-white/10" />
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-2.5 rounded-[2px] bg-white/70" />
                    <span className="h-1.5 w-3 rounded-[2px] bg-white/70" />
                  </span>
                </div>
                <video
                  key={previewUrl}
                  src={previewUrl!}
                  poster={template.thumbnailUrl || undefined}
                  className="aspect-[9/16] w-full bg-black object-contain"
                  autoPlay
                  controls
                  muted
                  playsInline
                  aria-label={`${template.name} video`}
                  data-testid={`video-phone-preview-template-${template.id}`}
                />
                <div className="flex justify-center bg-black py-1.5">
                  <span className="h-1 w-20 rounded-full bg-white/40" />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TemplateCard({ template }: { template: VideoReferenceTemplate }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(template.name);
  const [brandId, setBrandId] = useState(template.brandId || "");
  const [grantsOpen, setGrantsOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const { data: ownerBrands = [] } = useQuery<TemplateOwnerBrand[]>({
    queryKey: [`${TEMPLATES_KEY}/brands`],
  });
  const assignmentCount = template.assignmentCount ?? template.assignedBrandCount ?? 0;
  const categorize = useMutation({
    mutationFn: (kind: ReferenceKind) => apiRequest("PATCH", `${TEMPLATES_KEY}/${template.id}/kind`, {
      kind, brandId: template.brandId ?? null,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] }),
    onError: (error: Error) => toast({ title: "Category update failed", description: error.message, variant: "destructive" }),
  });
  const patch = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Enter a template name.");
      await apiRequest("PATCH", `${TEMPLATES_KEY}/${template.id}`, {
        name: trimmed,
        brandId: brandId || null,
      });
    },
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
    onError: (error: Error) => toast({ title: "Rename failed", description: error.message, variant: "destructive" }),
  });
  const archive = useMutation({
    mutationFn: () => apiRequest("POST", `${TEMPLATES_KEY}/${template.id}/archive`),
    onSuccess: () => {
      setArchiveOpen(false);
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast({ title: "Template archived" });
    },
    onError: (error: Error) => toast({ title: "Archive failed", description: error.message, variant: "destructive" }),
  });

  const cancelEditing = () => {
    setName(template.name);
    setBrandId(template.brandId || "");
    setEditing(false);
  };

  return (
    <Card className={template.archivedAt ? "opacity-60" : ""} data-testid={`card-video-reference-template-${template.id}`}>
      <CardContent className="space-y-3 p-3">
        <TemplatePreview template={template} />
        <div className="space-y-2">
          {editing ? (
            <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); patch.mutate(); }}>
              <div className="space-y-1">
                <Label htmlFor={`template-name-${template.id}`} className="text-[11px]">Name</Label>
                <Input
                  id={`template-name-${template.id}`}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={255}
                  autoFocus
                  className="h-8 text-sm"
                  data-testid={`input-template-name-${template.id}`}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`template-owner-${template.id}`} className="text-[11px]">Brand access</Label>
                <OwnerCombobox
                  id={`template-owner-${template.id}`}
                  value={brandId}
                  brands={ownerBrands}
                  onChange={setBrandId}
                  compact
                  testId={`select-template-owner-${template.id}`}
                />
              </div>
              <div className="flex gap-1">
                <Button type="submit" size="sm" className="h-8" disabled={patch.isPending}>Save</Button>
                <Button type="button" size="sm" variant="ghost" className="h-8" onClick={cancelEditing}>Cancel</Button>
              </div>
            </form>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{template.name}</p>
                <p className="truncate text-[10px] text-muted-foreground">{template.fileName || "Video file"}</p>
              </div>
              {!template.archivedAt && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setEditing(true)}
                  aria-label={`Edit ${template.name}`}
                  data-testid={`button-edit-template-${template.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="text-[10px]">{brandAccessLabel(template)}</Badge>
            <Badge variant="secondary" className="text-[10px]">
              {formatDuration(template.durationSec ?? template.referenceDurationSec)}
            </Badge>
            {template.archivedAt && <Badge variant="destructive" className="text-[10px]">Archived</Badge>}
          </div>
          <KindControl
            kind={template.kind}
            testId={`select-template-kind-${template.id}`}
            disabled={!!template.archivedAt || categorize.isPending}
            onChange={(kind) => categorize.mutateAsync(kind)}
          />
          {!template.brandId && <p className="text-[11px] text-muted-foreground">This category applies to the global template for every brand with access.</p>}
          <p className="text-xs text-muted-foreground" data-testid={`text-template-assignments-${template.id}`}>
            {template.brandId
              ? "Available only to this brand"
              : assignmentCount === 0
                ? "Available to every brand"
                : "Available only to assigned brands"}
          </p>
          <div className="flex justify-between gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => setGrantsOpen(true)}
              disabled={!!template.archivedAt || !!template.brandId}
              data-testid={`button-manage-template-grants-${template.id}`}
            >
              <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Manage brands
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => setArchiveOpen(true)}
              disabled={archive.isPending || !!template.archivedAt}
              data-testid={`button-archive-template-${template.id}`}
            >
              {archive.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="mr-1.5 h-3.5 w-3.5" />}
              {!archive.isPending && "Archive"}
            </Button>
          </div>
        </div>
      </CardContent>
      <BrandGrantsDialog template={template} open={grantsOpen} onOpenChange={setGrantsOpen} />
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent data-testid={`dialog-archive-template-${template.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this template?</AlertDialogTitle>
            <AlertDialogDescription>
              “{template.name}” will no longer be available for new assignments. Existing assignments and generated videos are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archive.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                archive.mutate();
              }}
              disabled={archive.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid={`button-confirm-archive-template-${template.id}`}
            >
              {archive.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

type BrandReferenceUpload = {
  id: string;
  brandId: string;
  fileName: string | null;
  url: string;
  kind: string | null;
  createdAt: string | null;
};

function UploadKindRow({ upload }: { upload: BrandReferenceUpload }) {
  const { toast } = useToast();
  const key = `/api/admin/video-reference-uploads/${upload.brandId}`;
  const categorize = useMutation({
    mutationFn: (kind: ReferenceKind) => apiRequest("PATCH", `${key}/${upload.id}/kind`, { kind }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [key] }),
    onError: (error: Error) => toast({ title: "Category update failed", description: error.message, variant: "destructive" }),
  });
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2" data-testid={`row-reference-upload-${upload.id}`}>
      <a className="max-w-[65%] truncate text-sm underline" href={upload.url} target="_blank" rel="noopener noreferrer">
        {upload.fileName || "Reference video"}
      </a>
      <KindControl kind={upload.kind} testId={`select-upload-kind-${upload.id}`}
        disabled={categorize.isPending} onChange={(kind) => categorize.mutateAsync(kind)} />
    </div>
  );
}

function BrandUploadsPanel() {
  const [brandId, setBrandId] = useState("");
  const { data: brands = [] } = useQuery<TemplateOwnerBrand[]>({ queryKey: [`${TEMPLATES_KEY}/brands`] });
  const key = `/api/admin/video-reference-uploads/${brandId}`;
  const { data: uploads = [], error, isLoading } = useQuery<BrandReferenceUpload[]>({
    queryKey: [key],
    enabled: !!brandId,
    queryFn: async () => (await apiRequest("GET", key)).json(),
  });
  return (
    <section className="space-y-3 rounded-md border p-4" aria-label="Brand reference uploads">
      <h4 className="text-sm font-medium">Brand reference uploads</h4>
      <label className="flex items-center gap-2 text-sm">
        Brand
        <select className="h-9 min-w-40 rounded-md border bg-background px-2" value={brandId}
          onChange={(event) => setBrandId(event.target.value)} data-testid="select-upload-brand">
          <option value="">Choose a brand</option>
          {brands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
        </select>
      </label>
      {brandId && (error ? <p role="alert" className="text-sm text-destructive">{(error as Error).message}</p>
        : isLoading ? <p className="text-sm text-muted-foreground">Loading uploads…</p>
        : uploads.length ? <div className="max-h-80 space-y-2 overflow-y-auto">{uploads.map((upload) =>
          <UploadKindRow key={upload.id} upload={upload} />)}</div>
        : <p className="text-sm text-muted-foreground">No reference uploads for this brand.</p>)}
    </section>
  );
}

function CreateTemplateForm() {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [brandId, setBrandId] = useState("");
  const { data: ownerBrands = [] } = useQuery<TemplateOwnerBrand[]>({
    queryKey: [`${TEMPLATES_KEY}/brands`],
  });
  const selectFile = (nextFile?: File | null) => {
    setFile(nextFile ?? null);
    if (!nextFile && inputRef.current) inputRef.current.value = "";
  };
  const create = useMutation({
    mutationFn: async () => {
      if (!name.trim() || !file) throw new Error("Enter a name and choose a video.");
      const uploadBody = new FormData();
      uploadBody.append("video", file);
      const uploadResponse = await fetch(`${TEMPLATES_KEY}/upload`, {
        method: "POST",
        body: uploadBody,
        credentials: "include",
      });
      if (!uploadResponse.ok) {
        const details = await uploadResponse.json().catch(() => ({ message: "Template upload failed" }));
        throw new Error(details.message || "Template upload failed");
      }
      const uploaded = await uploadResponse.json() as TemplateUploadResponse;
      const response = await apiRequest("POST", TEMPLATES_KEY, {
        name: name.trim(),
        brandId: brandId || null,
        referenceVideoUrl: uploaded.referenceVideoUrl,
        referenceDurationSec: uploaded.referenceDurationSec,
        thumbnailUrl: uploaded.thumbnailUrl,
      });
      return response.json();
    },
    onSuccess: () => {
      setName("");
      setFile(null);
      setBrandId("");
      if (inputRef.current) inputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      toast({ title: "Template created" });
    },
    onError: (error: Error) => toast({ title: "Upload failed", description: error.message, variant: "destructive" }),
  });

  return (
    <form
      className="grid gap-4 rounded-xl border bg-card p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-[minmax(11rem,1fr)_minmax(13rem,1fr)_minmax(18rem,1.3fr)_auto] lg:items-end"
      onSubmit={(event) => { event.preventDefault(); create.mutate(); }}
      data-testid="form-create-video-reference-template"
    >
      <div className="space-y-1.5">
        <Label htmlFor="video-reference-template-name" className="text-xs">Template name</Label>
        <Input
          id="video-reference-template-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Product close-up"
          maxLength={120}
          disabled={create.isPending}
          className="h-10"
          data-testid="input-create-template-name"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="video-reference-template-owner" className="text-xs">Brand access</Label>
        <OwnerCombobox
          id="video-reference-template-owner"
          value={brandId}
          brands={ownerBrands}
          onChange={setBrandId}
          disabled={create.isPending}
          testId="select-video-reference-template-owner"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="video-reference-template-file" className="text-xs">Reference video</Label>
        <input
          ref={inputRef}
          id="video-reference-template-file"
          type="file"
          accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
          onChange={(event) => selectFile(event.target.files?.[0])}
          disabled={create.isPending}
          className="sr-only"
          data-testid="input-create-template-file"
        />
        <div
          role="button"
          tabIndex={create.isPending ? -1 : 0}
          aria-label="Choose or drop a reference video"
          onClick={() => !create.isPending && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (!create.isPending && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragEnter={(event) => {
            event.preventDefault();
            if (!create.isPending) setIsDraggingFile(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!create.isPending) {
              event.dataTransfer.dropEffect = "copy";
              setIsDraggingFile(true);
            }
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsDraggingFile(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setIsDraggingFile(false);
            if (!create.isPending) selectFile(event.dataTransfer.files?.[0]);
          }}
          className={[
            "flex h-14 cursor-pointer items-center gap-3 rounded-lg border border-dashed px-3 transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            isDraggingFile
              ? "border-primary bg-primary/10"
              : file
                ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
                : "border-input bg-background hover:border-primary/50 hover:bg-muted/40",
            create.isPending ? "cursor-not-allowed opacity-60" : "",
          ].join(" ")}
          data-testid="dropzone-create-template-file"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10">
            {file ? <FileVideo className="h-4 w-4 text-primary" /> : <Upload className="h-4 w-4 text-primary" />}
          </div>
          <div className="min-w-0 flex-1">
            {file ? (
              <>
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB · Click to choose another
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold">Drop a video here</p>
                <p className="text-xs text-muted-foreground">or click to open · MP4, MOV, or WebM</p>
              </>
            )}
          </div>
          {file && !create.isPending ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label="Remove selected video"
              onClick={(event) => {
                event.stopPropagation();
                selectFile(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
      <Button
        type="submit"
        size="sm"
        className="h-10 whitespace-nowrap bg-violet-600 px-5 text-white hover:bg-violet-700"
        disabled={!name.trim() || !file || create.isPending}
        data-testid="button-create-video-reference-template"
      >
        {create.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
        {create.isPending ? "Uploading…" : "Create template"}
      </Button>
    </form>
  );
}

export function VideoReferenceTemplateLibrary() {
  const [page, setPage] = useState(1);
  const { data: templates = [], isLoading, error, refetch, isFetching } = useQuery<VideoReferenceTemplate[]>({
    queryKey: [TEMPLATES_KEY],
    queryFn: async () => {
      const response = await apiRequest("GET", TEMPLATES_KEY);
      return unwrapTemplates(await response.json());
    },
  });
  const pageCount = Math.max(1, Math.ceil(templates.length / TEMPLATE_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedTemplates = useMemo(
    () => templates.slice((safePage - 1) * TEMPLATE_PAGE_SIZE, safePage * TEMPLATE_PAGE_SIZE),
    [safePage, templates],
  );
  const rangeStart = templates.length === 0 ? 0 : (safePage - 1) * TEMPLATE_PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * TEMPLATE_PAGE_SIZE, templates.length);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  return (
    <section className="space-y-4" aria-labelledby="video-reference-template-library-title" data-testid="section-video-reference-template-library">
      <div>
        <h3 id="video-reference-template-library-title" className="flex items-center gap-2 text-base font-medium">
          <Film className="h-4 w-4" /> Video Reference Ad Templates
        </h3>
        <p className="text-sm text-muted-foreground">
          Create and manage reusable reference video templates, assign them to brands, and see where they’re in use.
        </p>
      </div>
      <CreateTemplateForm />
      <BrandUploadsPanel />
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
        </div>
      ) : error ? (
        <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="font-medium text-destructive">Failed to load templates</p>
          <p className="text-xs text-muted-foreground">{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>Retry</Button>
        </div>
      ) : templates.length === 0 ? (
        <p className="rounded-md border p-6 text-center text-sm text-muted-foreground" data-testid="text-no-video-reference-templates">
          No master templates have been uploaded.
        </p>
      ) : (
        <div className="space-y-4">
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="grid-video-reference-templates"
          >
            {pagedTemplates.map((template) => <TemplateCard key={template.id} template={template} />)}
          </div>
          {pageCount > 1 && (
            <div
              className="flex flex-col gap-3 border-t pt-4 text-sm sm:flex-row sm:items-center sm:justify-between"
              data-testid="pagination-video-reference-templates"
            >
              <p className="text-muted-foreground">
                Showing {rangeStart}–{rangeEnd} of {templates.length} templates
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={safePage <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  data-testid="button-template-page-previous"
                >
                  Previous
                </Button>
                <span className="min-w-20 text-center text-muted-foreground">
                  Page {safePage} of {pageCount}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                  data-testid="button-template-page-next"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}